import React, { useState } from 'react';

import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';

import { Card, CardTitle } from 'components/Card';
import Message from 'components/Message';
import { intlMessage } from 'constants/propTypes';
import { bem } from 'helpers/class-name';
import { IA_KEY } from 'helpers/ia-logic';
import useOrderNotificationPrefs from 'hooks/useOrderNotificationPrefs';
import { selectActiveVenue, selectIaLogic } from 'selectors/general';

import messages from './Notifications.messages';

const { element } = bem('notifications');

const AlertRow = ({ id, title, description, checked, disabled, onChange }) => (
  <label className={element('email-row')} htmlFor={id}>
    <span className={element('email-row-text')}>
      <Message as="span" className={element('email-row-title')} message={title} />
      <Message as="span" className={element('email-row-description')} message={description} />
    </span>
    <input
      type="checkbox"
      id={id}
      className={element('email-checkbox')}
      checked={checked}
      disabled={disabled}
      onChange={({ target }) => onChange(target.checked)}
    />
  </label>
);

AlertRow.propTypes = {
  id: PropTypes.string.isRequired,
  title: intlMessage.isRequired,
  description: intlMessage.isRequired,
  checked: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  onChange: PropTypes.func.isRequired,
};

const OrderNotificationsSection = () => {
  const { venueId } = useSelector(selectActiveVenue);
  const { featureIsSupported } = useSelector(state => selectIaLogic(state, IA_KEY.SKU_ORDERS, venueId));
  const { prefs, updatePrefs } = useOrderNotificationPrefs();
  const [permission, setPermission] = useState(() =>
    typeof Notification === 'undefined' ? null : Notification.permission
  );

  if (!featureIsSupported) return null;

  const handleBrowserNotificationsChange = checked => {
    if (!checked) {
      updatePrefs({ browserNotificationsEnabled: false });
      return;
    }

    if (permission === 'granted') {
      updatePrefs({ browserNotificationsEnabled: true });
      return;
    }

    Notification.requestPermission().then(nextPermission => {
      setPermission(nextPermission);
      updatePrefs({ browserNotificationsEnabled: nextPermission === 'granted' });
    });
  };

  return (
    <Card className={`${element('card')} ${element('order-alerts-card')}`}>
      <Message as={CardTitle} message={messages.orderNotificationsTitle} />
      <AlertRow
        id="orderNotificationSound"
        title={messages.orderNotificationsSoundTitle}
        description={messages.orderNotificationsSoundDescription}
        checked={prefs.soundEnabled}
        onChange={checked => updatePrefs({ soundEnabled: checked })}
      />
      {permission && (
        <AlertRow
          id="orderNotificationBrowser"
          title={messages.orderNotificationsBrowserTitle}
          description={
            permission === 'denied'
              ? messages.orderNotificationsPermissionDenied
              : messages.orderNotificationsBrowserDescription
          }
          checked={prefs.browserNotificationsEnabled && permission === 'granted'}
          disabled={permission === 'denied'}
          onChange={handleBrowserNotificationsChange}
        />
      )}
    </Card>
  );
};

export default OrderNotificationsSection;
